import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Attribute, AttributeType } from "gql/generated/types";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";

const initialAttributes: Attribute = {
  id: "",
  title: "",
  parent: [],
  type: AttributeType.String
}

type AdditionalSettingsProps = {
  attributes: Attribute[];
  setAttributes: Dispatch<SetStateAction<Attribute[]>>
}

const AdditionalProjectSettings: React.FC<AdditionalSettingsProps> = ({attributes, setAttributes}) => {
  const [groups, setGroups] = useState<string[]>([]);
  const [groupName, setGroupName] = useState<string>("");

  useEffect(() => {
    setGroups([...new Set(attributes?.map(ob => ob?.parent!.toString()))]);
  }, [attributes]);

  const handleChange = (attribute : Attribute, updatedField: Record<string, string|string[]>) => {
    setAttributes(prev =>
      prev.map((ob, i) =>
        i === prev.findIndex(a => a.id === attribute.id) ? { ...ob, ...updatedField } : ob
      )
    )
  }

  const renderInput = (body: Attribute, options: ColumnBodyOptions) => (
    <InputText
      value={body[options.field as "title" | "id"] ?? ""}
      type="text"
      placeholder={options.field}
      onChange={e => handleChange(body, { [options.field]: e.target.value })}
    />
  );

  const renderTypeInput = (body: Attribute) => (
    <div className="flex flex-column gap-2">
      <Dropdown
        value={body.type}
        options={Object.values(AttributeType)}
        onChange={e => handleChange(body, { type: e.value })}
      />
      {body.type === AttributeType.Enum && (
        <Chips
          value={body.enums ?? []}
          placeholder={T("action.add-values")}
          onChange={e => handleChange(body, { enums: e.value })}
        />
      )}
      {body.type === AttributeType.Link && (
        <InputText
          value={body.reading ?? ""}
          placeholder={T("action.write-link-value", { id: "{id}"})}
          onChange={e => handleChange(body, { reading: e.target.value })}
        />
      )}
    </div>
  );

  const deleteBtn = (body: Attribute) => (
    <Button
        icon='pi pi-trash'
        className='p-button-danger p-button-text p-button-rounded p-button-raised'
        onClick={() => setAttributes(prev => prev.toSpliced(prev.findIndex(ob => ob.id === body.id), 1))}
      />
  )

  return (
    <>
      <Accordion>
        {groups.length > 0 &&
          groups.map(group => (
            <AccordionTab key={group} header={group}>
              <DataTable value={attributes.filter(ob => ob?.parent?.toString() === group)}>
                <Column field="title" header={T("word.translation-key")} body={renderInput} />
                <Column field="id" header="Id" body={renderInput} />
                <Column field="type" header={T("word.type")} body={renderTypeInput} style={{ maxWidth: "40rem" }} />
                <Column body={deleteBtn} />
              </DataTable>
              <div className="flex mt-3">
                <Button
                  icon="pi pi-plus"
                  className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
                  title={T("action.add-attribute")}
                  onClick={() =>
                    setAttributes(prev => [...prev, { ...initialAttributes, parent: [group] }])
                  }
                />
              </div>
            </AccordionTab>
          ))}
      </Accordion>
      <div className="flex mt-3">
        <div className="p-inputgroup w-3">
          <InputText
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            placeholder={T("mdm.setting-group-name")}
            className="w-15rem"
          />
          <Button
            type="button"
            icon="pi pi-plus"
            className="p-inputgroup-addon "
            title={T("action.add-group")}
            onClick={() => {
              if (!groupName) return;
              setGroups(prev => [...prev, groupName]);
              setGroupName("");
            }}
          />
        </div>
      </div>
    </>
  );
}

export default AdditionalProjectSettings;